import { useEffect, useState, type InputHTMLAttributes } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { PaymentAccountResponse } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { DefinitionList, Section } from '../services/components';
import { formatDateTime } from '../services/format';
import { usePaymentAccount, useSetPaymentAccount } from './api';

type PaymentAccount = NonNullable<PaymentAccountResponse['paymentAccount']>;

/**
 * Payment Account (/app/payment-account): the one organisation-level receiving bank account that
 * renewal and service payments are transferred to. Exactly the three fields the API holds. It is
 * not a user, a Bekebi User ID, a ledger or a payment processor. Setting it needs
 * `payment_account.manage`; the API enforces that and audits every change.
 */
export function PaymentAccountPage() {
  const account = usePaymentAccount();
  const canManage = useHasPermission('payment_account.manage');

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1>Payment Account</h1>
        <p className="mt-1 text-sm text-muted">
          The bank account that renewal and service payments are transferred to. There is one for
          Bekebi Resources.
        </p>
      </div>

      {account.isPending ? (
        <LoadingState label="Loading the Payment Account…" />
      ) : account.isError ? (
        <ErrorState
          title="The Payment Account could not be loaded"
          message={account.error.message}
          onRetry={() => void account.refetch()}
        />
      ) : (
        <>
          {account.data ? (
            <Section title="Receiving account">
              <DefinitionList
                items={[
                  [
                    'Account name',
                    <span key="n" className="break-words">
                      {account.data.bankAccountName}
                    </span>,
                  ],
                  [
                    'Bank',
                    <span key="b" className="break-words">
                      {account.data.bankName}
                    </span>,
                  ],
                  [
                    'Account number',
                    <span key="a" className="break-all font-mono">
                      {account.data.bankAccountNumber}
                    </span>,
                  ],
                  ['Last changed', formatDateTime(account.data.updatedAt)],
                ]}
              />
            </Section>
          ) : (
            // Admin can set it; a view-only role (ICT) is told who does — information, not a task.
            <EmptyState
              title={canManage ? 'Payment Account not configured.' : 'Payment Account not set'}
              description={
                canManage
                  ? 'No receiving bank account has been recorded yet. Set it below.'
                  : 'Not set by the Admin yet.'
              }
            />
          )}
          {/* One form in one place for both cases, so its confirmation survives the first save. */}
          {canManage ? (
            <Section
              title={account.data ? 'Change the Payment Account' : 'Set the Payment Account'}
              description="All three fields are required. Saving replaces the current details."
            >
              <PaymentAccountForm account={account.data} />
            </Section>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Set / change — PUT /api/payment-account (same limits as the API contract)
// ---------------------------------------------------------------------------------------------

const bankField = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `Enter the ${label}.`)
    .max(200, `The ${label} can be at most 200 characters.`);

const formSchema = z.object({
  bankAccountName: bankField('account name'),
  bankName: bankField('bank name'),
  bankAccountNumber: bankField('account number'),
});
type FormFields = z.infer<typeof formSchema>;

const formValues = (account: PaymentAccount | null): FormFields => ({
  bankAccountName: account?.bankAccountName ?? '',
  bankName: account?.bankName ?? '',
  bankAccountNumber: account?.bankAccountNumber ?? '',
});

function PaymentAccountForm({ account }: { account: PaymentAccount | null }) {
  const save = useSetPaymentAccount();
  const [notice, setNotice] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: zodResolver(formSchema),
    defaultValues: formValues(account),
  });

  // Show the API's values whenever a new version arrives (without re-mounting the form).
  const version = account?.updatedAt ?? null;
  useEffect(() => {
    reset(formValues(account));
  }, [version]);

  const onSubmit = handleSubmit((values) => {
    setNotice(null);
    const current = formValues(account);
    if (
      account &&
      values.bankAccountName === current.bankAccountName &&
      values.bankName === current.bankName &&
      values.bankAccountNumber === current.bankAccountNumber
    ) {
      setNotice('No changes to save.');
      return;
    }
    save.mutate(values, { onSuccess: () => setNotice('The Payment Account was saved.') });
  });

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Payment Account details" className="space-y-4">
      <Field
        id="payment-account-name"
        label="Account name"
        error={errors.bankAccountName?.message}
        inputProps={register('bankAccountName')}
      />
      <Field
        id="payment-account-bank"
        label="Bank name"
        error={errors.bankName?.message}
        inputProps={register('bankName')}
      />
      <Field
        id="payment-account-number"
        label="Account number"
        error={errors.bankAccountNumber?.message}
        inputProps={{ ...register('bankAccountNumber'), spellCheck: false }}
      />
      {save.isError ? (
        <p role="alert" className="text-sm text-danger">
          {save.error.message}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="text-sm text-success">
          {notice}
        </p>
      ) : null}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : 'Save Payment Account'}
      </Button>
    </form>
  );
}

function Field({
  id,
  label,
  error,
  inputProps,
}: {
  id: string;
  label: string;
  error: string | undefined;
  inputProps: InputHTMLAttributes<HTMLInputElement> & UseFormRegisterReturn;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-fg-2">
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${id}-error` : undefined}
        // Bank details are not offered for browser autofill history.
        autoComplete="off"
        className="mt-1 block w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 py-2 text-sm"
        {...inputProps}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
